/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import './GlobalTooltipHost.css';

type TooltipPlacement = 'top' | 'bottom';
type TooltipSource = 'data' | 'title';
type InteractionMode = 'pointer' | 'focus';

interface TooltipPosition {
  top: number;
  left: number;
  arrowLeft: number;
  placement: TooltipPlacement;
}

interface ActiveTrigger {
  element: Element;
  text: string;
  source: TooltipSource;
  originalTitle: string | null;
  originalDescribedBy: string | null;
  originalAriaLabel: string | null;
  addedAriaLabel: boolean;
  pointerActive: boolean;
  focusActive: boolean;
}

interface TooltipState {
  text: string;
}

const VIEWPORT_MARGIN = 12;
const TOOLTIP_GAP = 9;
const HOVER_DELAY_MS = 260;
const TOOLTIP_SELECTOR = '[data-qp-tooltip], [title]';

function normalizeTooltipText(value: string | null): string {
  return value?.replace(/\s+/g, ' ').trim() ?? '';
}

function resolveTooltip(element: Element): { text: string; source: TooltipSource } | null {
  if (element.closest('[data-qp-tooltip-disabled="true"]')) return null;

  const dataText = normalizeTooltipText(element.getAttribute('data-qp-tooltip'));
  if (dataText) return { text: dataText, source: 'data' };

  const titleText = normalizeTooltipText(element.getAttribute('title'));
  if (titleText) return { text: titleText, source: 'title' };

  return null;
}

function findTooltipTrigger(target: EventTarget | null): Element | null {
  if (!(target instanceof Element)) return null;
  if (target.closest('[role="tooltip"], [data-qp-tooltip-host="true"]')) return null;
  return target.closest(TOOLTIP_SELECTOR);
}

function mergeDescribedBy(existing: string | null, tooltipId: string): string {
  return Array.from(new Set(`${existing ?? ''} ${tooltipId}`.trim().split(/\s+/).filter(Boolean))).join(' ');
}

function hasAccessibleName(element: Element): boolean {
  if (element.hasAttribute('aria-label') || element.hasAttribute('aria-labelledby')) return true;
  if (element instanceof HTMLInputElement && element.value.trim()) return true;
  if (element instanceof HTMLImageElement && element.alt.trim()) return true;
  if (element instanceof HTMLElement && element.innerText.trim()) return true;
  return false;
}

/**
 * Mevcut native `title` ve yeni `data-qp-tooltip` açıklamalarını tek bir
 * kurumsal, erişilebilir tooltip yüzeyine taşır. Olay delegasyonu sayesinde
 * sonradan render edilen ekranlar da ek entegrasyon gerektirmeden kapsanır.
 */
export function GlobalTooltipHost() {
  const reactId = useId();
  const tooltipId = `qp-global-tooltip-${reactId.replace(/:/g, '')}`;
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const activeRef = useRef<ActiveTrigger | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const [position, setPosition] = useState<TooltipPosition | null>(null);

  const clearHoverTimer = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  const restoreTrigger = useCallback((active: ActiveTrigger) => {
    const { element } = active;

    if (active.originalTitle !== null && !element.hasAttribute('title')) {
      element.setAttribute('title', active.originalTitle);
    }

    if (active.originalDescribedBy === null) element.removeAttribute('aria-describedby');
    else element.setAttribute('aria-describedby', active.originalDescribedBy);

    if (active.addedAriaLabel) {
      if (active.originalAriaLabel === null) element.removeAttribute('aria-label');
      else element.setAttribute('aria-label', active.originalAriaLabel);
    }
  }, []);

  const hide = useCallback(() => {
    clearHoverTimer();
    const active = activeRef.current;
    if (active) restoreTrigger(active);
    activeRef.current = null;
    setTooltip(null);
    setPosition(null);
  }, [clearHoverTimer, restoreTrigger]);

  const reveal = useCallback((active: ActiveTrigger, immediate: boolean) => {
    clearHoverTimer();
    const show = () => {
      if (activeRef.current !== active) return;
      setTooltip({ text: active.text });
    };

    if (immediate) show();
    else hoverTimerRef.current = setTimeout(show, HOVER_DELAY_MS);
  }, [clearHoverTimer]);

  const activate = useCallback((element: Element, mode: InteractionMode, immediate: boolean) => {
    const resolved = resolveTooltip(element);
    if (!resolved) return;

    let active = activeRef.current;
    if (active?.element !== element) {
      if (active) restoreTrigger(active);
      clearHoverTimer();
      setTooltip(null);
      setPosition(null);

      const originalTitle = element.getAttribute('title');
      const originalDescribedBy = element.getAttribute('aria-describedby');
      const originalAriaLabel = element.getAttribute('aria-label');
      const addedAriaLabel = !hasAccessibleName(element);

      if (originalTitle !== null) element.removeAttribute('title');
      element.setAttribute('aria-describedby', mergeDescribedBy(originalDescribedBy, tooltipId));
      if (addedAriaLabel) element.setAttribute('aria-label', resolved.text);

      active = {
        element,
        text: resolved.text,
        source: resolved.source,
        originalTitle,
        originalDescribedBy,
        originalAriaLabel,
        addedAriaLabel,
        pointerActive: false,
        focusActive: false,
      };
      activeRef.current = active;
    }

    if (mode === 'pointer') active.pointerActive = true;
    else active.focusActive = true;
    reveal(active, immediate);
  }, [clearHoverTimer, restoreTrigger, reveal, tooltipId]);

  const deactivateMode = useCallback((element: Element, mode: InteractionMode) => {
    const active = activeRef.current;
    if (!active || active.element !== element) return;

    if (mode === 'pointer') active.pointerActive = false;
    else active.focusActive = false;

    if (!active.pointerActive && !active.focusActive) hide();
  }, [hide]);

  const updatePosition = useCallback(() => {
    const active = activeRef.current;
    const tooltipElement = tooltipRef.current;
    if (!active || !tooltipElement) return;

    const topDialog = Array.from(document.querySelectorAll('[role="dialog"][aria-modal="true"]'))
      .filter(dialog => dialog.getClientRects().length > 0).at(-1);
    if (!active.element.isConnected || !active.element.getClientRects().length
      || active.element.closest('[hidden], [inert], [aria-hidden="true"]')
      || getComputedStyle(active.element).visibility === 'hidden'
      || topDialog && !topDialog.contains(active.element)) { hide(); return; }

    const anchorRect = active.element.getBoundingClientRect();
    const tooltipRect = tooltipElement.getBoundingClientRect();
    const placement: TooltipPlacement = anchorRect.top - VIEWPORT_MARGIN >= tooltipRect.height + TOOLTIP_GAP
      ? 'top'
      : 'bottom';
    const unclampedLeft = anchorRect.left + (anchorRect.width / 2) - (tooltipRect.width / 2);
    const maxLeft = Math.max(VIEWPORT_MARGIN, window.innerWidth - tooltipRect.width - VIEWPORT_MARGIN);
    const left = Math.min(Math.max(unclampedLeft, VIEWPORT_MARGIN), maxLeft);
    const rawTop = placement === 'top'
      ? anchorRect.top - tooltipRect.height - TOOLTIP_GAP
      : anchorRect.bottom + TOOLTIP_GAP;
    const maxTop = Math.max(VIEWPORT_MARGIN, window.innerHeight - tooltipRect.height - VIEWPORT_MARGIN);
    const top = Math.min(Math.max(rawTop, VIEWPORT_MARGIN), maxTop);
    const anchorCenter = anchorRect.left + (anchorRect.width / 2);
    const arrowLeft = Math.min(
      Math.max(anchorCenter - left, 14),
      Math.max(14, tooltipRect.width - 14),
    );

    setPosition(previous => previous?.top === top && previous.left === left
      && previous.arrowLeft === arrowLeft && previous.placement === placement
      ? previous : { top, left, arrowLeft, placement });
  }, [hide]);

  useLayoutEffect(() => {
    if (!tooltip) return;
    const anchor = activeRef.current?.element;
    if (!anchor) return;
    let frame = 0;
    const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(updatePosition); };
    // Centered dialogs can move when their asynchronously loaded body changes.
    // Observe the layout ancestors as well as the anchor's own dimensions.
    const resize = new ResizeObserver(schedule);
    for (let element: Element | null = anchor; element; element = element.parentElement) resize.observe(element);
    const mutations = new MutationObserver(records => {
      if (records.some(record => !(record.target instanceof Element
        && record.target.closest('[data-qp-tooltip-host="true"]')))) schedule();
    });
    mutations.observe(document.body, { subtree: true, childList: true, attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'inert', 'aria-hidden'], characterData: true });
    document.addEventListener('transitionend', schedule, true);
    document.addEventListener('animationend', schedule, true);
    schedule();
    return () => {
      cancelAnimationFrame(frame); resize.disconnect(); mutations.disconnect();
      document.removeEventListener('transitionend', schedule, true);
      document.removeEventListener('animationend', schedule, true);
    };
  }, [tooltip, updatePosition]);

  useEffect(() => {
    const handlePointerOver = (event: PointerEvent) => {
      if (event.pointerType === 'touch') return;
      const trigger = findTooltipTrigger(event.target);
      if (!trigger) return;
      if (event.relatedTarget instanceof Node && trigger.contains(event.relatedTarget)) return;
      activate(trigger, 'pointer', false);
    };

    const handlePointerOut = (event: PointerEvent) => {
      const active = activeRef.current;
      if (!active) return;
      if (event.relatedTarget instanceof Node && active.element.contains(event.relatedTarget)) return;
      deactivateMode(active.element, 'pointer');
    };

    const handleFocusIn = (event: FocusEvent) => {
      const trigger = findTooltipTrigger(event.target);
      if (trigger) activate(trigger, 'focus', true);
    };

    const handleFocusOut = (event: FocusEvent) => {
      const active = activeRef.current;
      if (!active) return;
      if (event.relatedTarget instanceof Node && active.element.contains(event.relatedTarget)) return;
      deactivateMode(active.element, 'focus');
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // This document listener runs before Modal's window bubble listener.
      // Only a rendered tooltip owns Escape; the next key can close the form.
      if (activeRef.current && tooltipRef.current && !event.defaultPrevented) {
        event.preventDefault();
        event.stopPropagation();
      }
      hide();
    };

    document.addEventListener('pointerover', handlePointerOver);
    document.addEventListener('pointerout', handlePointerOut);
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      document.removeEventListener('pointerover', handlePointerOver);
      document.removeEventListener('pointerout', handlePointerOut);
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      clearHoverTimer();
      const active = activeRef.current;
      if (active) restoreTrigger(active);
      activeRef.current = null;
    };
  }, [activate, clearHoverTimer, deactivateMode, hide, restoreTrigger, updatePosition]);

  if (!tooltip || typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={tooltipRef}
      id={tooltipId}
      className="qp-global-tooltip"
      role="tooltip"
      data-qp-tooltip-host="true"
      data-placement={position?.placement ?? 'top'}
      data-ready={position ? 'true' : 'false'}
      style={position ? {
        top: `${position.top}px`,
        left: `${position.left}px`,
        '--qp-global-tooltip-arrow-left': `${position.arrowLeft}px`,
      } as CSSProperties : undefined}
    >
      <span className="qp-global-tooltip-text">{tooltip.text}</span>
      <span className="qp-global-tooltip-arrow" aria-hidden />
    </div>,
    document.body,
  );
}
